import { _ as __awaiter } from './tslib.es6-61a17366.js';

/**
 * An implementation of rfc6749#section-4.1 and rfc7636.
 */
/**
 * To store the OAuth client's data between websites due to redirection.
 */
const LOCALSTORAGE_STATE = `-state`;
const LOCALSTORAGE_CODE = `-code`;
/**
 * Character set to generate code verifier defined in rfc7636.
 */
const PKCE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
/**
 * The maximum length for a code verifier for the best security we can offer.
 * Please note the NOTE section of RFC 7636 § 4.1 - the length must be >= 43,
 * but <= 128, **after** base64 url encoding. This means 32 code verifier bytes
 * encoded will be 43 bytes, or 96 bytes encoded will be 128 bytes. So 96 bytes
 * is the highest valid value that can be used.
 */
const RECOMMENDED_CODE_VERIFIER_LENGTH = 96;
/**
 * Translate the raw error strings returned from the server into error classes.
 */
function toErrorClass(rawError, description) {
  return new (RawErrorToErrorClassMap[rawError] || ErrorUnknown)(description);
}
/**
 * A list of OAuth2AuthCodePKCE errors.
 */
// To "namespace" all errors.
class ErrorOAuth2 {
  constructor(description) {
    this.description = description;
  }
}
// For really unknown errors.
class ErrorUnknown extends ErrorOAuth2 {
}
class ErrorInvalidJson extends ErrorOAuth2 {
}
// Errors that occur across many endpoints
class ErrorInvalidScope extends ErrorOAuth2 {
}
class ErrorInvalidRequest extends ErrorOAuth2 {
}
class ErrorInvalidToken extends ErrorOAuth2 {
}
/**
 * Possible authorization grant errors given by the redirection from the
 * authorization server.
 */
class ErrorAuthenticationGrant extends ErrorOAuth2 {
}
class ErrorUnauthorizedClient extends ErrorAuthenticationGrant {
}
class ErrorAccessDenied extends ErrorAuthenticationGrant {
}
class ErrorUnsupportedResponseType extends ErrorAuthenticationGrant {
}
class ErrorServerError extends ErrorAuthenticationGrant {
}
class ErrorTemporarilyUnavailable extends ErrorAuthenticationGrant {
}
/**
 * A list of possible access token response errors.
 */
class ErrorAccessTokenResponse extends ErrorOAuth2 {
}
class ErrorInvalidClient extends ErrorAccessTokenResponse {
}
class ErrorInvalidGrant extends ErrorAccessTokenResponse {
}
class ErrorUnsupportedGrantType extends ErrorAccessTokenResponse {
}
const RawErrorToErrorClassMap = {
  invalid_request: ErrorInvalidRequest,
  invalid_grant: ErrorInvalidGrant,
  unauthorized_client: ErrorUnauthorizedClient,
  access_denied: ErrorAccessDenied,
  unsupported_response_type: ErrorUnsupportedResponseType,
  invalid_scope: ErrorInvalidScope,
  server_error: ErrorServerError,
  temporarily_unavailable: ErrorTemporarilyUnavailable,
  invalid_client: ErrorInvalidClient,
  unsupported_grant_type: ErrorUnsupportedGrantType,
  invalid_json: ErrorInvalidJson,
  invalid_token: ErrorInvalidToken,
};
const Okta = {
  authorizationUrl: '/v1/authorize',
  logoutUrl: '/v1/logout',
  tokenUrl: '/v1/token',
  userInfoUrl: '/v1/userinfo',
  logoutRedirectParam: "post_logout_redirect_uri" /* Okta */,
};
const Auth0 = {
  authorizationUrl: '/authorize',
  logoutUrl: '/v2/logout',
  tokenUrl: '/oauth/token',
  userInfoUrl: '/userinfo',
  logoutRedirectParam: "returnTo" /* Auth0 */,
};
const Azure = {
  authorizationUrl: '/oauth2/v2.0/authorize',
  logoutUrl: '/oauth2/v2.0/logout',
  tokenUrl: '/oauth2/v2.0/token',
  userInfoUrl: '/oidc/userinfo',
  logoutRedirectParam: "post_logout_redirect_uri" /* Azure */,
};
const AzureUserInfoDomain = "https://graph.microsoft.com";
const LinkedIn = {
  authorizationUrl: '/oauth/v2/authorization',
  logoutUrl: '/oauth/v2/revoke',
  tokenUrl: '/oauth/v2/accessToken',
  userInfoUrl: '/v2/me',
  logoutRedirectParam: "redirect_uri" /* LinkedIn */,
};
const LinkedInUserInfoDomain = "https://api.linkedin.com";

/**
 * If opened in iframe, then redirect from parent's window as some IDPs block iframes eg linkedin
 * @returns boolean
 */
function inIframe() {
  try {
    return window.self !== window.top;
  }
  catch (e) {
    return true;
  }
}

class AuthService {
  constructor() {
    this.host = window.origin;
    this.state = {};
  }
  static get Instance() {
    // Do you need arguments? Make it a regular static method instead.
    return this._instance || (this._instance = new this());
  }
  authenticate(options, viewId) {
    return __awaiter(this, void 0, void 0, function* () {
      this.setConfig(viewId, {
        issuer: options.issuer ? options.issuer : "",
        endpoints: {
          authorizationUrl: options.configDetails && options.configDetails.AuthorizationUrl ? options.configDetails.AuthorizationUrl : "",
          tokenUrl: options.configDetails && options.configDetails.TokenUrl ? options.configDetails.TokenUrl : "",
          userInfoUrl: options.configDetails && options.configDetails.UserInfoUrl ? options.configDetails.UserInfoUrl : "",
          logoutUrl: options.configDetails && options.configDetails.LogoutUrl ? options.configDetails.LogoutUrl : "",
          logoutRedirectParam: options.configDetails && options.configDetails.LogoutRedirectParam ? options.configDetails.LogoutRedirectParam : "",
        },
        template: options.template ? options.template : "",
        clientId: options.clientId ? options.clientId : "",
        redirectUrl: this.host + '/oauth2callback',
        scopes: options.scopes || ['openid', 'profile'],
        onAccessTokenExpiry: undefined,
        onInvalidGrant(refreshAuthCodeOrRefreshToken) {
          return __awaiter(this, void 0, void 0, function* () {
            yield refreshAuthCodeOrRefreshToken(options);
          });
        },
      });
      return this.fetchAuthorizationCode(options, viewId);
    });
  }
  /**
   * getUser Info
   * @param viewId
   * @returns
   */
  userInfo(viewId) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
      this.setConfig(viewId, undefined);
      if (!((_b = (_a = this.state) === null || _a === void 0 ? void 0 : _a.accessToken) === null || _b === void 0 ? void 0 : _b.value)) {
        try {
          yield this.exchangeAuthCodeForAccessToken(viewId);
        }
        catch (error) {
          throw error;
        }
      }
      if (!this.state || !this.state.accessToken || !this.state.stateQueryParam)
        return null;
      const decodedState = JSON.parse(atob(this.state.stateQueryParam));
      const url = this.getUserInfoDomain(decodedState) + decodedState.endpoints.userInfoUrl;
      let res = yield fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.state.accessToken.value}`,
        },
      });
      return yield res.json();
    });
  }
  /**
  * Tells if the client is logged in or not. This means the client has at
  * least once successfully fetched an access token.
  */
  isLoggedIn(viewId) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
      this.setConfig(viewId, undefined);
      try {
        if (!((_b = (_a = this.state) === null || _a === void 0 ? void 0 : _a.accessToken) === null || _b === void 0 ? void 0 : _b.value)) {
          yield this.exchangeAuthCodeForAccessToken(viewId);
        }
        const isLoggedIn = !!this.state.accessToken && !this.isAccessTokenExpired();
        return isLoggedIn;
      }
      catch (error) {
        return false;
      }
    });
  }
  /**
   * get Id token
   * @param viewId
   * @returns id token
   */
  idToken(viewId) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
      this.setConfig(viewId, undefined);
      if (!((_b = (_a = this.state) === null || _a === void 0 ? void 0 : _a.accessToken) === null || _b === void 0 ? void 0 : _b.value) || this.hasIDTokenExpired()) {
        try {
          yield this.exchangeAuthCodeForAccessToken(viewId);
        }
        catch (error) {
          return undefined;
        }
      }
      return this.state.idToken;
    });
  }
  /**
   * Access token
   * @param viewId
   * @returns
   */
  accessToken(viewId) {
    var _a, _b, _c;
    return __awaiter(this, void 0, void 0, function* () {
      this.setConfig(viewId, undefined);
      if (!((_b = (_a = this.state) === null || _a === void 0 ? void 0 : _a.accessToken) === null || _b === void 0 ? void 0 : _b.value) || this.isAccessTokenExpired()) {
        try {
          yield this.exchangeAuthCodeForAccessToken(viewId);
        }
        catch (error) {
          return undefined;
        }
      }
      return (_c = this.state.accessToken) === null || _c === void 0 ? void 0 : _c.value;
    });
  }
  /**
   * logout
   * @param viewId
   * @param postLogoutRedirectUrl
   * @returns
   */
  logout(viewId, postLogoutRedirectUrl) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
      this.setConfig(viewId, undefined);
      if (!this.state || !this.state.stateQueryParam)
        return;
      const decodedState = JSON.parse(atob(this.state.stateQueryParam));
      if (decodedState.template === "LinkedIn" /* LinkedIn */) {
        throw 'Not Implemented!';
      }
      localStorage.removeItem(decodedState.viewId + LOCALSTORAGE_STATE);
      localStorage.removeItem(decodedState.viewId + LOCALSTORAGE_CODE);
      const url = decodedState.issuer +
        decodedState.endpoints.logoutUrl +
        `?id_token_hint=${this.state.idToken}` +
        `&${this.getPostLogoutRedirect(decodedState.template, postLogoutRedirectUrl, this.state.stateQueryParam ? this.state.stateQueryParam : "", (_a = decodedState.endpoints) === null || _a === void 0 ? void 0 : _a.logoutRedirectParam)}`;
      this.state = {};
      location.replace(url);
      return;
    });
  }
  getPostLogoutRedirect(template, redirectUrl, stateQueryParam, logoutRedirectParam = "") {
    switch (template) {
      case "Okta" /* Okta */:
        return `${this.getLogoutRedirectParam(template, logoutRedirectParam)}=${encodeURIComponent(redirectUrl)}/logout&state=${encodeURIComponent(stateQueryParam)}`;
      default:
        return `${this.getLogoutRedirectParam(template, logoutRedirectParam)}=${encodeURIComponent(redirectUrl)}/logout?state=${encodeURIComponent(stateQueryParam)}`;
    }
  }
  getLogoutRedirectParam(template, logoutRedirectParam) {
    if (logoutRedirectParam) {
      return logoutRedirectParam;
    }
    else {
      switch (template) {
        case "Auth0" /* Auth0 */:
          return "returnTo" /* Auth0 */;
        default:
          return "post_logout_redirect_uri" /* Okta */;
      }
    }
  }
  /**
   * Checks to see if the access token has expired.
   */
  isAccessTokenExpired() {
    const { accessToken } = this.state;
    return Boolean(accessToken && new Date() >= new Date(accessToken.expiry));
  }
  /**
   * Fetch an access token from the remote service. You may pass a custom
   * authorization grant code for any reason, but this is non-standard usage.
   */
  exchangeAuthCodeForAccessToken(viewId) {
    return __awaiter(this, void 0, void 0, function* () {
      const lsCode = localStorage.getItem(viewId + LOCALSTORAGE_CODE);
      let code;
      if (lsCode)
        code = JSON.parse(lsCode);
      let lsState = localStorage.getItem(viewId + LOCALSTORAGE_STATE);
      let state = {};
      if (lsState)
        state = JSON.parse(lsState);
      if (!code || !state) {
        throw 'Not Authenticated!';
      }
      this.state = state;
      let decodedState = {};
      if (this.state && this.state.stateQueryParam) {
        decodedState = JSON.parse(atob(this.state.stateQueryParam));
      }
      if (!decodedState.clientId) {
        throw 'Not Authenticated';
      }
      if (decodedState.template === "LinkedIn" /* LinkedIn */)
        throw 'Not Implemented!';
      this.setConfig(decodedState.viewId, {
        issuer: decodedState.issuer,
        endpoints: {
          authorizationUrl: decodedState.endpoints.authorizationUrl,
          tokenUrl: decodedState.endpoints.tokenUrl,
          logoutUrl: decodedState.endpoints.logoutUrl,
          userInfoUrl: decodedState.endpoints.userInfoUrl,
          logoutRedirectParam: decodedState.endpoints.logoutRedirectParam,
        },
        template: decodedState.template,
        clientId: decodedState.clientId,
        redirectUrl: decodedState.redirectUrl,
        scopes: decodedState.scopes || ['openid', 'profile'],
        onAccessTokenExpiry: undefined,
        onInvalidGrant(refreshAuthCodeOrRefreshToken) {
          return __awaiter(this, void 0, void 0, function* () {
            yield refreshAuthCodeOrRefreshToken(decodedState);
          });
        },
      });
      this.assertStateAndConfigArePresent();
      const { authorizationCode = code, codeVerifier = '' } = this.state;
      const { clientId, onInvalidGrant, redirectUrl } = this.config;
      if (!codeVerifier) {
        console.warn('No code verifier is being sent.');
      }
      else if (!authorizationCode) {
        console.warn('No authorization grant code is being passed.');
      }
      const url = this.config.issuer + this.config.endpoints.tokenUrl;
      const body = `grant_type=authorization_code&` +
        `code=${encodeURIComponent(authorizationCode || '')}&` +
        `redirect_uri=${encodeURIComponent(redirectUrl)}&` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `code_verifier=${codeVerifier}`;
      return fetch(url, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }).then((res) => {
        const jsonPromise = res.json().catch((_) => ({ error: 'invalid_json' }));
        if (!res.ok) {
          return jsonPromise.then(({ error, error_description }) => {
            switch (error) {
              case 'invalid_grant':
                onInvalidGrant(() => this.fetchAuthorizationCode(this.state, viewId));
                break;
            }
            return Promise.reject(toErrorClass(error, error_description));
          });
        }
        return jsonPromise.then(({ access_token, id_token, expires_in, refresh_token, scope }) => {
          let scopes = [];
          this.state.hasAuthCodeBeenExchangedForAccessToken = true;
          const accessToken = {
            value: access_token,
            expiry: new Date(Date.now() + parseInt(expires_in) * 1000).toString(),
          };
          this.state.accessToken = accessToken;
          this.state.idToken = id_token;
          if (refresh_token) {
            const refreshToken = {
              value: refresh_token,
            };
            this.state.refreshToken = refreshToken;
          }
          if (scope) {
            // Multiple scopes are passed and delimited by spaces,
            // despite using the singular name "scope".
            scopes = scope.split(' ');
            this.state.scopes = scopes;
          }
          localStorage.setItem(viewId + LOCALSTORAGE_STATE, JSON.stringify(this.state));
          return { token: accessToken, scopes };
        });
      });
    });
  }
  /**
   * If the state or config are missing, it means the client is in a bad state.
   * This should never happen, but the check is there just in case.
   */
  assertStateAndConfigArePresent() {
    if (!this.state || !this.config) {
      console.error('state:', this.state, 'config:', this.config);
      throw new Error('state or config is not set.');
    }
  }
  /**
   * Fetch an authorization grant via redirection. In a sense this function
   * doesn't return because of the redirect behavior (uses `location.replace`).
   */
  fetchAuthorizationCode(state, viewId) {
    return __awaiter(this, void 0, void 0, function* () {
      state.viewId = viewId;
      this.assertStateAndConfigArePresent();
      const base64EncodedState = (state === null || state === void 0 ? void 0 : state.stateQueryParam) ? state.stateQueryParam : btoa(JSON.stringify(Object.assign(Object.assign({}, state), { clientId: this.config.clientId, issuer: this.config.issuer, endpoints: this.config.endpoints, redirectUrl: this.config.redirectUrl })));
      const { clientId, redirectUrl, scopes } = this.config;
      const { codeChallenge, codeVerifier, } = yield AuthService.generatePKCECodes();
      const stateQueryParam = base64EncodedState;
      this.state = {
        codeChallenge,
        codeVerifier,
        stateQueryParam,
        isHTTPDecoratorActive: true,
      };
      localStorage.setItem(viewId + LOCALSTORAGE_STATE, JSON.stringify(this.state));
      const url = this.config.issuer +
        this.config.endpoints.authorizationUrl +
        `?response_type=code&` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUrl)}&` +
        `scope=${encodeURIComponent(scopes.join(' '))}&` +
        `state=${this.state.stateQueryParam}&` +
        `code_challenge=${encodeURIComponent(codeChallenge)}&` +
        `code_challenge_method=S256`;
      if (inIframe()) {
        window.parent.location.replace(url);
      }
      else {
        location.replace(url);
      }
    });
  }
  /**
 * Generates a code_verifier and code_challenge, as specified in rfc7636.
 */
  static generatePKCECodes() {
    const output = new Uint32Array(RECOMMENDED_CODE_VERIFIER_LENGTH);
    crypto.getRandomValues(output);
    const codeVerifier = AuthService.base64urlEncode(Array.from(output)
      .map((num) => PKCE_CHARSET[num % PKCE_CHARSET.length])
      .join(''));
    return crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(codeVerifier))
      .then((buffer) => {
      const hash = new Uint8Array(buffer);
      let binary = '';
      const hashLength = hash.byteLength;
      for (let i = 0; i < hashLength; i++) {
        binary += String.fromCharCode(hash[i]);
      }
      return binary;
    })
      .then(AuthService.base64urlEncode)
      .then((codeChallenge) => ({ codeChallenge, codeVerifier }));
  }
  setConfig(viewId, config) {
    this.recoverState(viewId);
    if (config) {
      this.config = config;
      this.config.endpoints = this.getEndpoints(this.config.template, this.config.endpoints);
    }
  }
  recoverState(viewId) {
    this.state = JSON.parse(localStorage.getItem(viewId + LOCALSTORAGE_STATE) || '{}');
    return this;
  }
  getEndpoints(template, endpoints) {
    switch (template) {
      case "Auth0" /* Auth0 */:
        return Auth0;
      case "Okta" /* Okta */:
        return Okta;
      case "Azure" /* Azure */:
        return Azure;
      case "LinkedIn" /* LinkedIn */:
        return LinkedIn;
      default:
        if (endpoints) {
          return endpoints;
        }
        return Okta;
    }
  }
  /**
   * Implements *base64url-encode* (RFC 4648 § 5) without padding, which is NOT
   * the same as regular base64 encoding.
   */
  static base64urlEncode(value) {
    let base64 = btoa(value);
    base64 = base64.replace(/\+/g, '-');
    base64 = base64.replace(/\//g, '_');
    base64 = base64.replace(/=/g, '');
    return base64;
  }
  getUserInfoDomain(state) {
    switch (state.template) {
      case "Azure" /* Azure */:
        return AzureUserInfoDomain;
      case "LinkedIn" /* LinkedIn */:
        return LinkedInUserInfoDomain;
      default:
        return state.issuer;
    }
  }
  hasIDTokenExpired() {
    if (!this.state.idToken)
      return true;
    const splitTok = this.state.idToken.split('.');
    if (splitTok.length != 3)
      return true;
    const dec = JSON.parse(atob(splitTok[1]));
    if (!dec['exp'])
      return true;
    return new Date() > new Date(parseInt(dec['exp']) * 1000);
  }
}
const Authservice = AuthService.Instance;

/**
 * AuhtConfigTemplate indentifies the auth service provider that needs to be used for authentication
 *
 * Usage example: Bridge.AuthConfigTemplate.Okta
 */
var AuthConfigTemplate;
(function (AuthConfigTemplate) {
  AuthConfigTemplate["Okta"] = "Okta";
  AuthConfigTemplate["Auth0"] = "Auth0";
  AuthConfigTemplate["Azure"] = "Azure";
  AuthConfigTemplate["LinkedIn"] = "LinkedIn";
})(AuthConfigTemplate || (AuthConfigTemplate = {}));
/**
 * The Auth object helps you to perform operations related to authentication
 *
 * This allows you to check if the user is authenticated, authenticate a user by passing in the auth config,
 * get the access_token or id_token of recently authenticated user and logout the user.
 *
 */
class Auth {
  constructor(viewid) {
    this._vid = '';
    this._vid = viewid;
  }
  /**
   * Helper to check if still authenticated.
   * The checks determines if an access token or id_token is available
   * and not expired. No further validation is applied. That should be
   * done by a backend.
   *
   * Usage example:
   * ```js
   * let authenticated = await Bridge.Auth.isAuthenticated()
   * ```
   */
  isLoggedIn() {
    return __awaiter(this, void 0, void 0, function* () {
      return yield Authservice.isLoggedIn(this._vid);
    });
  }
  /**
   * Returns the latest id token, if available.
   *
   * Usage example:
   * ```js
   * await Bridge.Auth.idToken()
   * ```
   */
  idToken() {
    return __awaiter(this, void 0, void 0, function* () {
      return yield Authservice.idToken(this._vid);
    });
  }
  /**
   * Returns the latest access token, if available.
   * Usage example:
   * ```js
   * await Bridge.Auth.accessToken()
   * ```
   */
  accessToken() {
    return __awaiter(this, void 0, void 0, function* () {
      return yield Authservice.accessToken(this._vid);
    });
  }
  /**
   * Logs out the user and redirects to the root route.
   *
   *
   * Usage example:
   * ```js
   * await Auth.logout()
   * ```
   */
  logOut(postLogoutRedirectUrl) {
    return __awaiter(this, void 0, void 0, function* () {
      return yield Authservice.logout(this._vid, postLogoutRedirectUrl);
    });
  }
  /**
   * Returns the user info of the logged in user.
   *
   * Usage example:
   * ```js
   * await Bridge.Auth.userInfo()
   * ```
   *
   */
  userInfo() {
    return __awaiter(this, void 0, void 0, function* () {
      return yield Authservice.userInfo(this._vid);
    });
  }
  /**
   * Authenticate the user by passing in an object of {@link AuthConfig}.
   *
   * Usage example:
   * ```js
   * await Auth.authenticate({
   *  template: AuthConfigTemplate.Okta
   *  issuer: 'https://{yourAuthDomain}/oauth2/default',
   *  clientId: '{client_id_of_the_application_registerd_in_auth_service_provider}'
   * })
   * ```
   */
  authenticate(options) {
    return __awaiter(this, void 0, void 0, function* () {
      return yield Authservice.authenticate(options, this._vid);
    });
  }
  /**
   * Callback function for completing auth flow.
   *
   * Usage example:
   * ```js
   * await Auth.handleCallback("codevalue", "statevalue")
   * ```
   */
  static handleCallback(code, state) {
    return __awaiter(this, void 0, void 0, function* () {
      let decodedState = JSON.parse(atob(state));
      localStorage.setItem(decodedState.viewId + LOCALSTORAGE_CODE, JSON.stringify(code));
      location.replace(window.origin + '/' + decodedState.viewId);
    });
  }
}

export { Auth as A, AuthConfigTemplate as a, inIframe as i };
